import { Component, ViewChild, Input } from '@angular/core';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { ReasonSelectComponent } from 'components/input/reason-select/reason-select.component';
import { CredentialType } from 'enums/CredentialType';
import { ReasonType } from 'enums/ReasonType';
import { EndAccountingPlanDtoModel } from 'models/generated/Accounting.Api.Models.CreditWizardRequest';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { NotificationService } from 'services/notification.service';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { BrandPipe } from 'pipes/brand.pipe';

@CredentialRequired(CredentialType.EditAccountingPlan)
@Component({
	templateUrl: './terminate-product-plans.component.html',
	providers: [FormValidationService, CompanyDataProviderFactory],
	imports: [Layout, Inputs, ReasonSelectComponent, TranslatePipe, BrandPipe]
})
export class TerminateProductPlansComponent {

	@ViewChild('terminationDateInput') terminationDateInput?: DateInputComponent;
	private _plansToTerminate: ProductPlanModel[] = [];

	reasonType = ReasonType;
	newTerminationDate: Date;
	maxTerminationDate: Date;
	selectedTerminationReason: ReasonModel | undefined;
	dataProvider?: CompanyDataProvider;
	endPlansNow = false;
	brandId?: string;
	planBrands: string[] = [];

	get plansToTerminate() { return this._plansToTerminate; }
	set plansToTerminate(plans: ProductPlanModel[]) {
		this._plansToTerminate = plans;
		for (const plan of plans) {
			if (plan.brandId && this.planBrands.indexOf(plan.brandId) < 0) {
				if (!this.brandId) this.brandId = plan.brandId;
				this.planBrands.push(plan.brandId);
			}
		}
	}

	get plansToTerminateNames() {
		return this.plansToTerminate.filter(plan => plan.brandId == this.brandId).map(plan => this.productNamePipe.transform(plan, true, true)).join('\n');
	}

	private _companyId!: string;
	@Input()
	set companyId(id: string) {
		this._companyId = id;
		this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(id);
	}
	get companyId() {
		return this._companyId;
	}

	constructor(
		private readonly accountingService: AccountingService,
		private readonly navigation: NavigationService,
		private readonly dataProviderFactory: CompanyDataProviderFactory,
		private readonly notificationService: NotificationService,
		private readonly productNamePipe: ProductNamePipe,
		private readonly dateService: DateService
	) {

		this.newTerminationDate = this.maxTerminationDate = dateService.today();
		setTimeout(() => {
			this.terminationDateInput!.valueChanged();
		});
	}

	async terminateProductPlans() {
		if (!this.newTerminationDate || !this.selectedTerminationReason) return;
		if (this.notificationService.confirm('company.product-plans.terminate-all.confirm')) {
			const plans = this.plansToTerminate.filter(plan => plan.brandId == this.brandId);
			const requests = plans
				.map(plan => this.accountingService.filterCurrentAccountingPlan(plan.accountingPlans))
				.filter(currentPlan => !!currentPlan)
				.map(currentPlan => {
					const request = new EndAccountingPlanDtoModel();
					request.accountingPlanId = currentPlan!.id;
					request.endDate = this.endPlansNow ? this.dateService.today() : currentPlan.endDate;
					request.terminationDate = this.newTerminationDate;
					request.terminationReasonId = this.selectedTerminationReason?.id;
					request.removeAllUnpostedInvoicePosts = false;

					return this.accountingService.endAccountingPlan(request);
				});

			await Promise.all(requests);

			this.navigation.closeView(this);
		}
	}

	cancel() {
		this.navigation.closeView(this);
	}
}
